import { useRef } from "react";

import {
  Chart as ChartJS,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from "chart.js";

import { Bar } from "react-chartjs-2";

import ChartToolbar from "./ChartToolbar";


// Register the Chart.js components required for this bar chart.
ChartJS.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);


function StationTotalEnergyChart({
  data = [],
  chargerType = "Both"
}) {
  const chartRef = useRef(null);


  const AC_COLOUR = "#2e7d32";
  const DC_COLOUR = "#f9a825";


  // Determine which charger types should be displayed.
  const showAC = chargerType === "Both" || chargerType === "AC";
  const showDC = chargerType === "Both" || chargerType === "DC";


  // Keep stations matching the current charger filter that contain
  // a valid total energy value.
  const stationData = Array.isArray(data)
    ? data.filter((row) => {
        const totalEnergy =
          Number(row.totalEnergy);

        const validType =
          (row.chargerType === "AC" && showAC) ||
          (row.chargerType === "DC" && showDC);

        return (
          validType &&
          !Number.isNaN(totalEnergy)
        );
      })
    : [];


  // Sort stations alphabetically.
  stationData.sort((a, b) => {
    const aName =
      a.locationName ||
      a.chargingStationId ||
      "Unknown Location";

    const bName =
      b.locationName ||
      b.chargingStationId ||
      "Unknown Location";

    return aName.localeCompare(bName);
  });


  const labels = stationData.map((row) => {
    const name =
      row.locationName ||
      row.chargingStationId ||
      "Unknown Location";

    return `${name} (${row.chargerType})`;
  });


  const chartData = {
    labels,
    datasets: [
      {
        label: "Total Energy Consumption",
        data: stationData.map((row) =>
          Number(row.totalEnergy) / 1000
        ),
        backgroundColor: stationData.map((row) =>
          row.chargerType === "DC"
            ? DC_COLOUR
            : AC_COLOUR
        ),
        borderColor: stationData.map((row) =>
          row.chargerType === "DC"
            ? DC_COLOUR
            : AC_COLOUR
        ),
        borderWidth: 1,
        barPercentage: 0.9,
        categoryPercentage: 0.85,
        maxBarThickness: 48
      }
    ]
  };


  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    indexAxis: "y",
    scales: {
      x: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Total Energy Consumption (MWh)"
        }
      },
      y: {
        ticks: {
          autoSkip: false
        },
        title: {
          display: true,
          text: "Charging Station"
        }
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          title: (items) => {
            if (!items.length) {
              return "";
            }

            return labels[items[0].dataIndex];
          },

          label: (context) => {
            const row =
              stationData[context.dataIndex];

            const totalEnergyMWh =
              Number(row.totalEnergy) / 1000;

            return [
              `Total energy: ${totalEnergyMWh.toFixed(3)} MWh`,
              `Sessions: ${Number(
                row.sessionCount
              ).toLocaleString()}`
            ];
          }
        }
      }
    }
  };


  return (
    <article className="chart-card">
      <div className="chart-card-header">
        <div className="chart-card-title">
          <h2>
            Total Energy Consumption by Station
          </h2>

          <p>
            Compare total energy consumed across individual
            charging stations.
          </p>
        </div>


        <ChartToolbar
          chartRef={chartRef}
          title="Total Energy Consumption by Station"
          enableZoom={false}
        />
      </div>


      <div className="chart-canvas-container">
        {stationData.length > 0 ? (
          <Bar
            ref={chartRef}
            data={chartData}
            options={options}
          />
        ) : (
          <div className="chart-empty-state">
            No station energy data available for the selected filters.
          </div>
        )}
      </div>
    </article>
  );
}


export default StationTotalEnergyChart;